import {
  Children,
  createContext,
  isValidElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type AnchorHTMLAttributes,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
} from "react";

interface BrowserLocation {
  pathname: string;
  search: string;
  hash: string;
}

interface NavigateOptions {
  replace?: boolean;
}

interface RouterValue {
  location: BrowserLocation;
  navigate: (to: string, options?: NavigateOptions) => void;
}

interface RouteProps {
  path: string;
  element: ReactNode;
}

const RouterContext = createContext<RouterValue | null>(null);
const ParamsContext = createContext<Record<string, string>>({});

function browserLocation(): BrowserLocation {
  return {
    pathname: window.location.pathname || "/",
    search: window.location.search,
    hash: window.location.hash,
  };
}

function internalUrl(to: string): URL {
  const url = new URL(to, window.location.origin);
  if (url.origin !== window.location.origin || !url.pathname.startsWith("/")) {
    throw new Error("Navigation is limited to same-origin application paths");
  }
  return url;
}

function matchPath(pattern: string, pathname: string): Record<string, string> | null {
  if (pattern === "*") return {};
  const patternParts = pattern.split("/").filter(Boolean);
  const pathParts = pathname.split("/").filter(Boolean);
  if (patternParts.length !== pathParts.length) return null;
  const params: Record<string, string> = {};
  for (let index = 0; index < patternParts.length; index += 1) {
    const patternPart = patternParts[index];
    const pathPart = pathParts[index];
    if (!patternPart || !pathPart) return null;
    if (patternPart.startsWith(":")) {
      try {
        params[patternPart.slice(1)] = decodeURIComponent(pathPart);
      } catch {
        return null;
      }
    } else if (patternPart !== pathPart) {
      return null;
    }
  }
  return params;
}

function useRouter(): RouterValue {
  const router = useContext(RouterContext);
  if (!router) throw new Error("Router hooks must be used within BrowserRouter");
  return router;
}

export function BrowserRouter({ children }: { children: ReactNode }) {
  const [location, setLocation] = useState(browserLocation);
  useEffect(() => {
    const onPopState = () => setLocation(browserLocation());
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);
  const navigate = useCallback((to: string, options: NavigateOptions = {}) => {
    const url = internalUrl(to);
    const target = `${url.pathname}${url.search}${url.hash}`;
    if (options.replace) window.history.replaceState(null, "", target);
    else window.history.pushState(null, "", target);
    setLocation(browserLocation());
  }, []);
  const value = useMemo(() => ({ location, navigate }), [location, navigate]);
  return <RouterContext.Provider value={value}>{children}</RouterContext.Provider>;
}

export function Route(props: RouteProps) {
  void props;
  return null;
}

export function Routes({ children }: { children: ReactNode }) {
  const { location } = useRouter();
  for (const child of Children.toArray(children)) {
    if (!isValidElement<RouteProps>(child)) continue;
    const params = matchPath(child.props.path, location.pathname);
    if (params) {
      return <ParamsContext.Provider value={params}>{child.props.element}</ParamsContext.Provider>;
    }
  }
  return null;
}

interface LinkProps extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href"> {
  to: string;
}

export function Link({ to, onClick, target, ...props }: LinkProps) {
  const { navigate } = useRouter();
  const href = internalUrl(to);
  const click = (event: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(event);
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      (target && target !== "_self")
    ) {
      return;
    }
    event.preventDefault();
    navigate(`${href.pathname}${href.search}${href.hash}`);
  };
  return (
    <a
      {...props}
      href={`${href.pathname}${href.search}${href.hash}`}
      target={target}
      onClick={click}
    />
  );
}

interface NavLinkProps extends LinkProps {
  end?: boolean;
}

export function NavLink({ className, end = false, to, ...props }: NavLinkProps) {
  const { location } = useRouter();
  const destination = internalUrl(to).pathname;
  const active = end
    ? location.pathname === destination
    : location.pathname === destination || location.pathname.startsWith(`${destination}/`);
  const classes = [className, active ? "active" : null].filter(Boolean).join(" ");
  return (
    <Link
      {...props}
      to={to}
      className={classes || undefined}
      aria-current={active ? "page" : undefined}
    />
  );
}

export function Navigate({ to, replace = false }: { to: string; replace?: boolean }) {
  const { navigate } = useRouter();
  useEffect(() => navigate(to, { replace }), [navigate, replace, to]);
  return null;
}

export function useLocation(): BrowserLocation {
  return useRouter().location;
}

export function useNavigate() {
  return useRouter().navigate;
}

export function useParams<T extends Record<string, string | undefined> = Record<string, string>>() {
  return useContext(ParamsContext) as T;
}

export function useSearchParams(): readonly [URLSearchParams] {
  const { location } = useRouter();
  return useMemo(() => [new URLSearchParams(location.search)] as const, [location.search]);
}

export type RouteElement = ReactElement<RouteProps>;
